// Read-only Data QA audit page for the forecasting self-serve flow: what a QA run
// blessed (per channel × platform × tactic × KPI group), at which grain, and why.
// Selection happens on the Request Forecast page; this page shows the shared-picker
// run (latest by default). Data is the QA pipeline's results JSON from S3; the
// drill-down charts lazy-fetch the sibling weekly series file (schema v2) on first
// open. The table carries one plain verdict per row (Readiness, see data-qa-copy);
// what it means and what to do live in the per-group drill-down.
//
// The page shell lives here; the results view and sub-components are in ./components,
// with shared helpers in data-qa-utils.
import './ForecastDataQa.scss';
import { Button } from '@Components/Button/Button';
import { Appearance } from '@Components/designConstants';
import { FullPageSpinner } from '@Components/Spinner';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { GoToTab } from '../../../utils/hooks/useNav';
import { DataQaConfig, PageTab, PageTabValue } from '../forecasting-constants';
import { DataQaRun, ForecastDataQaResult } from '../useForecastDataQa';
import { QaResults } from './components/QaResults';

interface ForecastDataQaProps {
  savedScope?: DataQaConfig;
  goToTab: GoToTab<PageTabValue>;
  /** Shared Data QA runs, fetched once at ForecastingPage. */
  qaState: ForecastDataQaResult;
  /** Resolved shared run selection (newest by default); null until runs load. */
  selectedRun: DataQaRun | null;
  onSelectRun: (s3Key: string) => void;
}

const ForecastDataQa = ({
  savedScope,
  goToTab,
  qaState,
  selectedRun,
  onSelectRun
}: ForecastDataQaProps) => {
  const { cid: company } = useCompanyInfo();

  // useCompanyInfo returns cid '' until hydrated — hold the loading state until
  // we have a real company.
  const loadingState = (
    <div className='dq-state' data-testid='dataqa-loading'>
      <FullPageSpinner />
    </div>
  );

  const renderContent = () => {
    if (!company) {
      return loadingState;
    }
    switch (qaState.status) {
      case 'empty':
        return (
          <div className='dq-state' data-testid='dataqa-empty'>
            <p>No Data QA run found for this client yet.</p>
            <p>
              Just started one? You&rsquo;ll get an email when it&rsquo;s done (a first run can take
              about an hour); refresh this page then.
            </p>
            <Button
              appearance={Appearance.OUTLINED}
              onClick={() => goToTab(PageTab.FORECAST_ONBOARDING)}
            >
              Set up forecasting
            </Button>
          </div>
        );
      case 'error':
        return (
          <div className='dq-state' data-testid='dataqa-error'>
            <p>Couldn&rsquo;t load the Data QA results: {qaState.message}</p>
          </div>
        );
      case 'ready': {
        // Non-null whenever status is 'ready'; this guard is for the type only.
        if (!selectedRun) {
          return loadingState;
        }
        return (
          <QaResults
            qa={selectedRun.data}
            s3Key={selectedRun.s3Key}
            runs={qaState.runs}
            onSelectRun={onSelectRun}
            onRefresh={qaState.refetch}
            isRefreshing={qaState.isRefreshing}
            savedScope={savedScope}
          />
        );
      }
      default:
        return loadingState;
    }
  };

  return (
    <div className='forecast-data-qa' data-testid='forecast-data-qa'>
      {renderContent()}
    </div>
  );
};

export { ForecastDataQa };
